/* External */
import {
  memo,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Set } from 'immutable';

/* Ott-common */
import { EntityName } from '@dtvgo/backend-service/normalize';

/* Components */
import LanderLayout from '../LanderLayout';
import VirtualizedList, { ItemDataProvider } from '../VirtualizedList';
import LanderRow from './LanderRow';
import LanderSkeleton from '../LanderSkeleton';

/* Utils */
import { useLoading } from '../../providers/LoadingProvider';
import useFetchPage from '../../hooks/useFetchPage';
import useResetFocus from '../../hooks/useResetFocus';
import {
  SOURCE_CAROUSEL_ID,
  STARTED_PAGE_ID,
} from '../../utils/constants';
import {
  getCardType,
  getCarouselHeight,
  getInitialCardType,
  sizeByViewport,
  CardType,
  BannerType,
} from '../../utils/cardsUtils';
import { useOpenSidebar } from '../../providers/SidebarStateContext';
import { getIsPagesV4OrV5Active, getIsCurableBannerActive } from '../../utils/functions';

/* Hooks */
import useGetUpSellBannerMessage from '../../hooks/useGetUpSellBannerMessage';

/* Styles */
import './LanderPage.scss';
import usePageCarousels from '../../hooks/usePageCarousels';

const landerPageHeight = sizeByViewport(1080);

function LanderPage({
  className = '',
  pageId,
  sourceProvider = undefined,
  children = undefined,
  preContent = undefined,
  onScroll = undefined,
  initialMaxOffset = 0,
  withoutFeatured = false,
  onFetchRecentlyWatched = undefined,
  withoutTitle = false,
}) {
  const [scrolled, setScrolled] = useState(false);
  const [position, setPosition] = useState(false);
  const [fetchedCarousels, setFetchedCarousels] = useState(() => Set());
  const [wasCarouselFocused, setWasCarouselFocused] = useState(false);
  const [initialScrollPosition] = useState(() => Number.parseFloat(sessionStorage.getItem('offsetFromStartedPage') || 0));
  const { hideLoading } = useLoading();
  const goToSidebar = useOpenSidebar();
  const setAllSessionStorage = useResetFocus(pageId);
  const [initialSectionIndexEpg] = useState(() => Number.parseFloat(sessionStorage.getItem('initialSectionIndexEpg') || 0));
  const [parentalBlockedChannelId] = useState(() => sessionStorage.getItem('parentalBlockedChannelId') || '');
  const isCurableBannerActive = getIsCurableBannerActive();
  const { showUpSellBannerCarousel } = useGetUpSellBannerMessage(isCurableBannerActive) || {};

  const positionsList = useRef();
  const getPositionsList = useCallback(() => {
    if (!positionsList.current) {
      positionsList.current = [];
    }
    return positionsList.current;
  }, []);
  const translateValuesList = useRef();
  const getTranslateValuesList = useCallback(() => {
    if (!translateValuesList.current) {
      translateValuesList.current = [];
    }
    return translateValuesList.current;
  }, []);

  const { page, isFetching } = useFetchPage(pageId, sourceProvider);
  const carouselsInfo = getIsPagesV4OrV5Active() ? page?.elements : page?.carousels;
  const carouselsFromStore = usePageCarousels(page);

  // It should be removed from the code when pages v4 is the final version.
  const carousels = useMemo(() => {
    if (isCurableBannerActive || !showUpSellBannerCarousel) return carouselsInfo;

    return carouselsInfo?.insert(1, { type: BannerType.UPSELL_BANNER });
  }, [carouselsInfo, showUpSellBannerCarousel, isCurableBannerActive]);

  const isRecentlyWatchedInCarousels = useMemo(() => !!carousels?.find(
    (carousel) => carousel?.sourceCarouselId === SOURCE_CAROUSEL_ID.RECENTLY_WATCHED,
  ), [carousels]);

  const initialSectionIndex = useMemo(
    () => (wasCarouselFocused || initialSectionIndexEpg || parentalBlockedChannelId ? undefined : Number.parseFloat(sessionStorage.getItem('initialSectionIndex')) || 0),
    [initialSectionIndexEpg, parentalBlockedChannelId, wasCarouselFocused],
  );

  const updatePositionCarousel = useCallback((carouselIndex, newCarouselPosition) => {
    getPositionsList()[carouselIndex] = newCarouselPosition;
  }, [getPositionsList]);

  const updateTranslate = useCallback((carouselIndex, newCarouselPosition) => {
    getTranslateValuesList()[carouselIndex] = newCarouselPosition;
  }, [getTranslateValuesList]);

  const handleCarouselFetchComplete = useCallback((carouselId) => {
    setFetchedCarousels((current) => current.add(carouselId));
  }, []);

  const updateWasCarouselFocused = useCallback(() => {
    setWasCarouselFocused(true);
  }, []);

  const getCardTypeByIndex = useCallback((index) => {
    const carouselInfo = carousels?.get(index);

    // It should be removed from the code when pages v4 is the final version.
    const entityType = isCurableBannerActive
      ? EntityName.BANNER
      : BannerType.UPSELL_BANNER;

    if (carouselInfo?.type === entityType) return CardType.UPSELL_BANNER;

    const carouselFromStore = showUpSellBannerCarousel
      ? carouselsFromStore?.get(index === 0 ? 0 : index - 1)
      : carouselsFromStore?.get(index);

    const cardType = getCardType(carouselFromStore, withoutFeatured);
    const initialCardType = getInitialCardType(carouselInfo, withoutFeatured);

    return carouselFromStore ? cardType : initialCardType;
  }, [
    carouselsFromStore,
    carousels,
    withoutFeatured,
    showUpSellBannerCarousel,
    isCurableBannerActive,
  ]);

  const getItemSize = useCallback((index) => {
    const cardType = getCardTypeByIndex(index);
    return sizeByViewport(
      getCarouselHeight(cardType) ?? -1,
    );
  }, [getCardTypeByIndex]);

  const initialSectionIndexToUse = useMemo(() => {
    if (
      !carousels
        || (!initialSectionIndex && initialSectionIndex !== 0)
    ) return initialSectionIndex;

    let index = initialSectionIndex;
    while (getItemSize(index) === 0 && index < carousels?.size) index += 1;
    return index;
  }, [carousels, getItemSize, initialSectionIndex]);

  const itemData = useMemo(() => ({
    carouselsInfo: carousels,
    pageId,
    sourceProvider,
    scrolled,
    noFeatured: withoutFeatured,
    noTitle: withoutTitle,
    fetchedCarousels,
    getPositionsList,
    getTranslateValuesList,
    initialSectionIndex: initialSectionIndexToUse,
    goToSidebar,
    handleCarouselFetchComplete,
    updateWasCarouselFocused,
    updatePositionCarousel,
    updateTranslate,
  }), [
    withoutFeatured,
    withoutTitle,
    carousels,
    pageId,
    sourceProvider,
    scrolled,
    fetchedCarousels,
    getPositionsList,
    getTranslateValuesList,
    initialSectionIndexToUse,
    goToSidebar,
    handleCarouselFetchComplete,
    updatePositionCarousel,
    updateTranslate,
    updateWasCarouselFocused,
  ]);

  useEffect(() => {
    if (pageId !== sessionStorage.getItem(STARTED_PAGE_ID)) {
      setAllSessionStorage();
      setPosition(true);
      if (parentalBlockedChannelId) {
        sessionStorage.setItem('parentalBlockedChannelId', parentalBlockedChannelId);
        sessionStorage.setItem('initialSectionIndex', -1);
      }
    }
  }, [pageId, parentalBlockedChannelId, setAllSessionStorage]);

  useEffect(() => {
    hideLoading();
  }, [hideLoading]);

  const recentlyWatchedInFetchedCarousels = useCallback(() => {
    if (onFetchRecentlyWatched && fetchedCarousels?.has(SOURCE_CAROUSEL_ID.RECENTLY_WATCHED)) {
      onFetchRecentlyWatched(true);
    }
  }, [fetchedCarousels, onFetchRecentlyWatched]);

  useEffect(() => {
    if (onFetchRecentlyWatched && isRecentlyWatchedInCarousels) {
      onFetchRecentlyWatched(false);
    }
  }, [isRecentlyWatchedInCarousels, onFetchRecentlyWatched]);

  useEffect(() => {
    recentlyWatchedInFetchedCarousels();
  }, [recentlyWatchedInFetchedCarousels]);

  const handleScroll = useCallback((scrollOffset, maxScrollOffset) => {
    sessionStorage.setItem('offsetFromStartedPage', scrollOffset);
    setScrolled(!!scrollOffset);
    if (onScroll) onScroll(scrollOffset, maxScrollOffset);
  }, [onScroll]);

  return (
    isFetching
      ? (
        <LanderLayout>
          {preContent}
          <LanderSkeleton withoutFeatured={withoutFeatured} />
        </LanderLayout>
      )
      : (
        <LanderLayout className="dtv-html5-lander-page">
          {preContent}
          <ItemDataProvider itemData={itemData}>
            <VirtualizedList
              className={`dtv-html5-lander-page__list ${className}`}
              height={landerPageHeight}
              itemCount={carousels?.size || 0}
              itemSize={getItemSize}
              onScroll={handleScroll}
              renderer={LanderRow}
              initialSectionIndex={initialSectionIndex}
              startedPageId={pageId}
              initialScrollPosition={initialScrollPosition}
              resetScrollPosition={position}
              initialMaxOffset={initialMaxOffset}
            >
              {children}
            </VirtualizedList>
          </ItemDataProvider>
        </LanderLayout>
      )
  );
}

LanderPage.propTypes = {
  className: PropTypes.string,
  pageId: PropTypes.string.isRequired,
  sourceProvider: PropTypes.string,
  children: PropTypes.node,
  preContent: PropTypes.node,
  onScroll: PropTypes.func,
  initialMaxOffset: PropTypes.number,
  withoutFeatured: PropTypes.bool,
  onFetchRecentlyWatched: PropTypes.func,
  withoutTitle: PropTypes.bool,
};

export default memo(LanderPage);
